import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  TextField,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import GenericButton from "../../components/Custom/GenericButton";
import api from "../../globals/axios";
import { useAlert } from "../../components/Alert";
import { useTranslation } from "react-i18next";
import AdminDashboardCard from "../../components/Custom/AdminDashboardCard";
import CustomAppBar from "../../components/AppBar";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    minHeight: "100vh",
    marginBottom: "-4rem",
  },
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    padding: "20px 0",
    borderRadius: "4px",
    marginBottom: "20px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  statsContainer: {
    display: "flex",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    padding: "20px 0",
  },
  filterContainer: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  dateFilterContainer: {
    marginBottom: "20px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "4px",
    padding: "16px",
  },
  statCard: {
    flex: "1",
    minWidth: "180px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
  },
}));

const BiocharProcurements = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [procurements, setProcurement] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 0,
    total_objects: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    name: "",
    address: "",
    status:"",
    start_date: null,
    end_date: null,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Stats items
  const statsItems = [
    {
      label: t("Farmers"),
      value: stats.total_farmers || 0,
      color: "#00BCD4",
    },
    {
      label: t("Procured Quantity"),
      value: stats.total_procured_quantity || 0,
      color: "#FFEB3B",
    },
    {
      label: t("Total cost"),
      value: stats.total_cost || 0,
      color: "#FF5722",
    },
    {
      label: t("Avg Procurement cost"),
      value: stats.average_cost_per_procurement || 0,
      color: "#8BC34A",
    },
  ];

  // Define table columns
  const columns = [
    { key: "farmer_name", label: t("Farmer Name") },
    { key: "mobile", label: t("Mobile") },
    { key: "address", label: t("address") },
    { key: "status", label: t("Status") },
    { key: "quantity_kg", label: t("Quantity(kg)") },
    { key: "paid_amount", label: t("Paid Amount") },
    { key: "paid_on", label: t("Paid Date") },
    { key: "pickup_plant", label: t("Pickup Plant") },
  ];

  const fetchStats = async () => {
    let { start_date, end_date } = filters;
    let query = ``;
    if (start_date) query += `&start_date=${start_date}`;
    if (end_date) query += `&end_date=${end_date}`;

    try {
      const res = await api.get(
        `/app-admin/get-ca-biochar-procurements-stats/?${query}`
      );
      if (res && res.data) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
      showAlert("error", "Couldn't fetch the stats. Please try again later!");
    }
  };

  // Fetch Procurements data
  const fetchProcurementData = async (page = 1) => {
    setIsLoading(true);
    let { name, address, start_date, end_date, status } = filters;
    let query = `?page=${page}`;
    if (name) query += `&name=${name}`;
    if (address) query += `&address=${address}`;
    if(status) query += `&status=${status}`
    if (start_date) query += `&start_date=${start_date}`;
    if (end_date) query += `&end_date=${end_date}`;

    try {
      const res = await api.get(
        `/app-admin/get-ca-biochar-procurements/${query}`
      );
      if (res && res.data) {
        setProcurement(res.data.results);
        setPagination({
          page: res.data.current_page_number,
          total_pages: res.data.total_pages,
          total_objects: res.data.total_objects,
        });
      }
      setError("");
    } catch (err) {
      console.error("Error fetching Procurements:", err);
      setError("Couldn't fetch the Procurements data. Please try again later!");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    fetchProcurementData(value);
  };

  // Handle date filter
  const handleDateFilter = () => {
    const formattedStartDate = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : null;
    const formattedEndDate = endDate
      ? dayjs(endDate).format("YYYY-MM-DD")
      : null;

    setFilters({
      ...filters,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
    });
  };

  // Clear date filters
  const clearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFilters({
      ...filters,
      start_date: null,
      end_date: null,
    });
  };

  useEffect(() => {
    fetchStats();
    fetchProcurementData(1);
  }, [filters.start_date, filters.end_date]);

  useEffect(() => {
    fetchProcurementData(1);
    fetchStats();
  }, []);

  // handle detailed procurement view
  const handleDetailBiocharPageView = (procurement_id) => {
    navigate(`/procurement/${procurement_id}`);
  };

  return (
    <Box className={classes.root} component={Paper}>
      <CustomAppBar />

      <Box className={classes.container}>
        <Paper className={classes.title} elevation={0}>
          <Typography variant="h5">{t("Biochar Procurements")}</Typography>
        </Paper>

        {/* Date Range Filter */}
        <Box className={classes.statsContainer}>
          <Box className={classes.statCard}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("Start Date")}
                value={startDate}
                onChange={(date) => setStartDate(date)}
                renderInput={(params) => <TextField {...params} />}
                inputFormat="DD/MM/YYYY"
              />
            </LocalizationProvider>
          </Box>

          {/* End Date Picker */}
          <Box className={classes.statCard}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={t("End Date")}
                value={endDate}
                onChange={(date) => setEndDate(date)}
                renderInput={(params) => <TextField {...params} />}
                inputFormat="DD/MM/YYYY"
                minDate={startDate}
                disabled={!startDate}
              />
            </LocalizationProvider>
          </Box>

          {/* Apply Filter Button */}
          <Box className={classes.statCard}>
            <GenericButton
              backgroundColor={"#4CAF50"}
              fontColor={"#fff"}
              fontSize={"14px"}
              fullWidth
              padding={"15px 10px"}
              onClick={handleDateFilter}
              disabled={!startDate}
              sx={{ margin: "0px" }}
            >
              {t("Apply Filter")}
            </GenericButton>
          </Box>

          {/* Clear Button */}
          <Box className={classes.statCard}>
            <GenericButton
              backgroundColor={"#F44336"}
              fontColor={"#fff"}
              fontSize={"14px"}
              fullWidth
              padding={"15px 10px"}
              onClick={clearDateFilters}
              disabled={!startDate && !endDate}
              sx={{ margin: "0px" }}
            >
              {t("Clear")}
            </GenericButton>
          </Box>
        </Box>

        <Box className={classes.statsContainer}>
          {statsItems.map((stat, index) => (
            <AdminDashboardCard
              key={index}
              label={stat.label}
              value={stat.value}
              color={stat.color}
            />
          ))}
        </Box>

        <Box className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("Farmer Name")}
                value={filters.name}
                onChange={(e) =>
                  setFilters({ ...filters, name: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel id="status-label">{t("Status")}</InputLabel>
                <Select
                  labelId="status-label"
                  value={filters.status || ""}
                  label={t("Status")}
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                > 
                  <MenuItem value="">{t("All")}</MenuItem>
                  <MenuItem value="PLACED">
                    {t("Placed")}
                  </MenuItem>
                  <MenuItem value="INSPECTION_SCHEDULED">
                    {t("Inspection Scheduled")}
                  </MenuItem>
                  <MenuItem value="ACCEPTED">{t("Accepted")}</MenuItem>
                  <MenuItem value="REJECTED">{t("Rejected")}</MenuItem>
                  <MenuItem value="PICKUP_REQUESTED">
                    {t("Pickup Requested")}
                  </MenuItem>
                  <MenuItem value="PICKUP_SCHEDULED">
                    {t("Pickup Scheduled")}
                  </MenuItem>
                  <MenuItem value="PICKED">{t("Picked")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={t("Address")}
                value={filters.address}
                onChange={(e) =>
                  setFilters({ ...filters, address: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <GenericButton
                backgroundColor={"#4CAF50"}
                fontColor={"#fff"}
                fontSize={"14px"}
                fullWidth
                padding={"15px 10px"}
                onClick={() => fetchProcurementData(1)}
              >
                {t("Search")}
              </GenericButton>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper}>
          <Table sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.key}
                    align="center"
                    sx={{
                      borderRight: "1px solid rgba(224, 224, 224, 1)",
                      fontWeight: "bold",
                      backgroundColor: "#FFC107",
                      color: "white",
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#FFC107",
                    color: "white",
                    borderRight: "1px solid rgba(224, 224, 224, 1)",
                  }}
                >
                  {t("Action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {t("Loading")}...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {error}
                  </TableCell>
                </TableRow>
              ) : procurements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    {t("No records found")}
                  </TableCell>
                </TableRow>
              ) : (
                procurements.map((procurement, index) => (
                  <TableRow key={index}>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align="center"
                        sx={{
                          borderRight: "1px solid rgba(224, 224, 224, 1)",
                        }}
                      >
                        {procurement[column.key] || "-"}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <GenericButton
                        backgroundColor={"#4CAF50"}
                        fontColor={"#fff"}
                        fontSize={"12px"}
                        padding={"5px 10px"}
                        onClick={() =>
                          handleDetailBiocharPageView(procurement.id)
                        }
                      >
                        {t("View")}
                      </GenericButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className={classes.pagination}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            disabled={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default BiocharProcurements;
